import * as React from "react";
import {
  Paper,
  Stack,
  Typography,
  TextField,
  Button,
  Alert,
  Divider,
} from "@mui/material";
import { useNavigate, useSearchParams, useParams, useLocation } from "react-router-dom";
import { adminFetch } from "../../services/adminFetch";
import { useInstallPrompt } from "../../pwa/useInstallPrompt";
import { Snackbar } from "@mui/material";
import { useAdminServiceWorker } from "../../pwa/useAdminServiceWorker";

export default function AdminLoginPage() {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const { locale } = useParams<{ locale: string }>();
  const location = useLocation();

  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [err, setErr] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);

  const { isInstallable, promptInstall } = useInstallPrompt();

  const { updateAvailable, applyUpdate } = useAdminServiceWorker();

  // ✅ extra safety: show install only on this route
  const isAdminLoginPath = React.useMemo(() => {
    // matches /el/admin/login or /en/admin/login etc
    return /\/admin\/login\/?$/.test(location.pathname);
  }, [location.pathname]);

  const submit = async () => {
    setErr(null);
    setLoading(true);
    try {
      const res = await adminFetch("/api/admin/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });

      const data = await res.json().catch(() => ({}));
      console.log("LOGIN status:", res.status, "data:", data);

      if (!res.ok) {
        setErr(data?.error || "Login failed");
        return;
      }

      // ✅ debug: check immediately if cookie works
      const meRes = await adminFetch("/api/admin/auth/me");
      const meData = await meRes.json().catch(() => ({}));
      console.log("ME status:", meRes.status, "data:", meData);

      if (!meRes.ok) {
        setErr(
          "Login OK αλλά ΔΕΝ κρατήθηκε cookie (auth/me = 401). Δες CORS / host mismatch.",
        );
        return;
      }

      const next = sp.get("next");
      if (next) {
        nav(next, { replace: true });
        return;
      }

      nav(`/${locale || "el"}/admin/properties`, { replace: true });
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    // βρες το υπάρχον manifest
    const existing = document.querySelector('link[rel="manifest"]') as HTMLLinkElement | null;
    const prevHref = existing?.getAttribute("href") || null;

    // αν δεν υπάρχει, το δημιουργούμε
    let link = existing;
    if (!link) {
      link = document.createElement("link");
      link.rel = "manifest";
      document.head.appendChild(link);
    }

    // βάλε admin manifest
    link.setAttribute("href", "/manifest-admin.json");

    // cleanup: γύρνα στο προηγούμενο όταν φύγεις από login
    return () => {
      if (!link) return;
      if (prevHref) link.setAttribute("href", prevHref);
      else link.remove();
    };
  }, []);

  return (
    <Stack alignItems="center" sx={{ py: 6 }}>
      <Paper
        variant="outlined"
        sx={{ p: 3, borderRadius: 3, width: "100%", maxWidth: 420 }}
      >
        <Stack spacing={2}>
          <Typography variant="h5" sx={{ fontWeight: 900 }}>
            Admin Login
          </Typography>

          {/* ✅ Install only here */}
          {isAdminLoginPath && isInstallable ? (
            <Alert
              severity="info"
              action={
                <Button
                  variant="contained"
                  onClick={async () => {
                    const res = await promptInstall();
                    console.log("Install outcome:", res?.outcome);
                  }}
                  sx={{ fontWeight: 900 }}
                >
                  Install
                </Button>
              }
            >
              Εγκατάσταση Admin στο κινητό (PWA).
            </Alert>
          ) : null}

          {err ? <Alert severity="error">{err}</Alert> : null}

          <TextField
            label="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="username"
          />
          <TextField
            label="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
          />

          <Button
            variant="contained"
            onClick={submit}
            disabled={loading}
            sx={{ fontWeight: 900 }}
          >
            {loading ? "Signing in..." : "Sign in"}
          </Button>

          <Divider />

          <Typography variant="caption" color="text.secondary">
            Tip: Αν είναι iPhone/Safari, το Install γίνεται από Share → Add to Home Screen.
          </Typography>
        </Stack>
      </Paper>
      <Snackbar
        open={updateAvailable}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="info"
          variant="filled"
          action={
            <Button color="inherit" size="small" onClick={applyUpdate} sx={{ fontWeight: 900 }}>
              Update
            </Button>
          }
          sx={{ fontWeight: 800 }}
        >
          Υπάρχει νέα έκδοση του Admin. Πάτα Update για ενημέρωση.
        </Alert>
      </Snackbar>
    </Stack>
  );
}
